const COMMON_REFRESH_RATES = [
  24, 30, 48, 50, 60, 72, 75, 90, 100, 120, 144, 165, 180, 200, 240,
];

export type DisplayCadence = {
  estimatedRefreshRateHz: number;
  expectedFrameMs: number;
};

export function estimateDisplayCadence(
  frameDurations: number[]
): DisplayCadence | null {
  const durations = frameDurations
    .filter(
      duration => Number.isFinite(duration) && duration >= 3 && duration <= 50
    )
    .sort((a, b) => a - b);
  if (durations.length < 8) return null;

  // The faster fifth approximates the display cadence without teaching the
  // estimator that jank-induced slower frames are a lower-refresh display.
  const baseline = durations[Math.floor((durations.length - 1) * 0.2)];
  const measuredRate = 1000 / baseline;
  const nearestCommonRate = COMMON_REFRESH_RATES.reduce((nearest, rate) =>
    Math.abs(rate - measuredRate) < Math.abs(nearest - measuredRate)
      ? rate
      : nearest
  );
  const estimatedRefreshRateHz =
    Math.abs(nearestCommonRate - measuredRate) / measuredRate <= 0.08
      ? nearestCommonRate
      : Math.round(measuredRate);

  return {
    estimatedRefreshRateHz,
    expectedFrameMs: 1000 / estimatedRefreshRateHz,
  };
}

export function frameDurationsFromTimestamps(timestamps: number[]): number[] {
  return timestamps
    .slice(1)
    .map((timestamp, index) => timestamp - timestamps[index]);
}

export function countEstimatedDroppedFrames(
  frameDurations: number[],
  expectedFrameMs: number
) {
  if (!Number.isFinite(expectedFrameMs) || expectedFrameMs <= 0) return 0;
  return frameDurations.reduce(
    (count, duration) =>
      count + Math.max(0, Math.round(duration / expectedFrameMs) - 1),
    0
  );
}

export function evaluatePanTestValidity({
  cadenceAvailable,
  minimumCoverage,
  movementUpdates,
  panDurationMs,
  targetDurationMs,
}: {
  cadenceAvailable: boolean;
  minimumCoverage: number;
  movementUpdates: number;
  panDurationMs: number;
  targetDurationMs: number;
}) {
  const panCoverage =
    targetDurationMs > 0 ? panDurationMs / targetDurationMs : 0;
  const invalidReasons = [
    ...(panCoverage < minimumCoverage
      ? [`pan-coverage-below-${minimumCoverage}`]
      : []),
    ...(movementUpdates < 10 ? ['insufficient-viewport-updates'] : []),
    ...(!cadenceAvailable ? ['display-cadence-unavailable'] : []),
  ];
  return {
    invalidReasons,
    panCoverage,
    valid: invalidReasons.length === 0,
  };
}
